import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { ListResult } from '../api/core/list-result';
import { Company, CreateUpdateCompany, CompanyItem, CompanySummary, CompanyLocation, CompanyDashboardRelation, CompanyMetrics, CompanyContact } from '../api/company/company';
import { RecruitmentTemplate } from '../api/company/recruitment-template';
import { BACKEND_URL } from '../app.config';
import { JobSummary, Position, PositionListItem, PW, PWListItem, PwSummary, Recruitment, RecruitmentListItem, RecruitmentSummary, ForeignEntity, CodeDto } from '../api/data-model';

@Injectable()
export class CompanyService {
  constructor(private readonly http: HttpClient, @Inject(BACKEND_URL) private readonly baseUrl: string, private readonly router: Router) { }

  /**
   * Handle 404 error by navigating to not found page
   * @param code The business code that was not found
   * @returns An empty Observable that never completes
   */
  private handle404Error<T>(code: string): (error: HttpErrorResponse) => Observable<T> {
    return (error: HttpErrorResponse) => {
      if (error.status === 404) {
        // Navigate to 404 page with context information
        this.router.navigate(['/notfound'], {
          queryParams: {
            type: 'company',
            code: code,
            message: `Company with code "${code}" not found`
          }
        });
        // Return an empty Observable that never completes to prevent further processing
        return new Observable<T>(() => { });
      }
      return throwError(() => error);
    };
  }

  getIdByCode(code: string): Observable<Company> {
    return this.http.get<Company>(this.baseUrl + '/api/app/company/id-by-code?code=' + code).pipe(
      catchError(this.handle404Error<Company>(code))
    );
  }

  getCode(id: string): Observable<CodeDto> {
    return this.http.get(this.baseUrl + `/api/app/company/${id}/code`);
  }

  getByCode(code: string): Observable<Company> {
    return this.http.get<Company>(this.baseUrl + '/api/app/company/by-code?code=' + code).pipe(
      catchError(this.handle404Error<Company>(code))
    );
  }

  getCompany(id: string): Observable<Company> {
    return this.http.get<Company>(`${this.baseUrl}/api/app/company/${id}`);
  }

  addCompany(company: CreateUpdateCompany): Observable<Company> {
    return this.http.post<Company>(`${this.baseUrl}/api/app/company`, company);
  }

  getCompanyMetrics(id: string): Observable<CompanyMetrics> {
    return this.http.get<CompanyMetrics>(`${this.baseUrl}/api/app/company/${id}/metrics`);
  }

  getList(filter: any, sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<CompanyItem>> {
    return this.http.get<ListResult<CompanyItem>>(this.baseUrl + '/api/app/company', {
      params: {
        businessCode: filter.businessCode ?? '',
        companyName: filter.companyName ?? '',
        signingPersonFirstName: filter.signingPersonFirstName ?? '',
        signingPersonLastName: filter.signingPersonLastName ?? '',
        contactPersonName: filter.contactPersonName ?? '',
        emailAddress: filter.emailAddress ?? '',
        activeStatus: filter.activeStatus ?? '',
        partnerCompanyType: filter.partnerCompanyType ?? '',
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  exportToCsv(filter: any, sorting: string): Observable<Blob> {
    return this.http.get(this.baseUrl + '/api/app/company/exporting', {
      responseType: 'blob',
      params: {
        businessCode: filter.businessCode ?? '',
        companyName: filter.companyName ?? '',
        signingPersonFirstName: filter.signingPersonFirstName ?? '',
        signingPersonLastName: filter.signingPersonLastName ?? '',
        contactPersonName: filter.contactPersonName ?? '',
        emailAddress: filter.emailAddress ?? '',
        activeStatus: filter.activeStatus ?? '',
        partnerCompanyType: filter.partnerCompanyType ?? '',
        sorting,
      },
    });
  }

  getSummary(id: string): Observable<CompanySummary> {
    return this.http.get<CompanySummary>(`${this.baseUrl}/api/app/company/${id}/summary`);
  }

  getLocations(id: string, sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<CompanyLocation>> {
    return this.http.get<ListResult<CompanyLocation>>(`${this.baseUrl}/api/app/company/${id}/locations`, {
      params: {
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  getRelations(id: string): Observable<CompanyDashboardRelation[]> {
    return this.http.get<CompanyDashboardRelation[]>(`${this.baseUrl}/api/app/company/${id}/relations`);
  }

  getPositions(id: string, filter: any, sorting: string = '', skipCount: number = 0, maxResultCount: number = 10): Observable<ListResult<PositionListItem>> {
    return this.http.get<ListResult<PositionListItem>>(`${this.baseUrl}/api/app/company/${id}/jobs`, {
      params: {
        jobTitle: filter.jobTitle ?? '',
        worksiteCity: filter.worksiteCity ?? '',
        jobStatus: filter.jobStatus ?? '',
        minUSDegree: filter.minUSDegree ?? '',
        altUSDegree: filter.altUSDegree ?? '',
        suggestedSOCCode: filter.suggestedSOCCode ?? '',
        sorting,
        skipCount: skipCount.toString(),
        maxResultCount: maxResultCount.toString(),
      },
    });
  }

  getPosition(companyId: string, id: string): Observable<Position> {
    return this.http.get<Position>(`${this.baseUrl}/api/app/company/${companyId}/job/${id}`);
  }

  addPosition(position: Position): Observable<Position> {
    return this.http.post<Position>(`${this.baseUrl}/api/app/company/${position.companyId}/job`, position);
  }

  updatePosition(position: Position): Observable<Position> {
    return this.http.put<Position>(`${this.baseUrl}/api/app/company/${position.companyId}/job/${position.id}`, position);
  }

  getPositionSummary(companyId: string, id: string): Observable<JobSummary> {
    return this.http.get<JobSummary>(`${this.baseUrl}/api/app/company/${companyId}/job/${id}/summary`);
  }

  getPWs(id: string, jobId: string, filter: any, sorting: string = '', skipCount: number = 0, maxResultCount: number = 10): Observable<ListResult<PWListItem>> {
    return this.http.get<ListResult<PWListItem>>(`${this.baseUrl}/api/app/company/${id}/pws`, {
      params: {
        jobId,
        pwrNo: filter.pwrNo ?? '',
        jobTitle: filter.jobTitle ?? '',
        pwStatus: filter.pwStatus ?? '',
        sorting,
        skipCount: skipCount.toString(),
        maxResultCount: maxResultCount.toString(),
      },
    });
  }

  getPWSummary(companyId: string, id: string): Observable<PwSummary> {
    return this.http.get<PwSummary>(`${this.baseUrl}/api/app/company/${companyId}/pw/${id}/summary`);
  }

  getPW(companyId: string, id: string): Observable<PW> {
    return this.http.get<PW>(`${this.baseUrl}/api/app/company/${companyId}/pw/${id}`);
  }

  addPW(pw: PW): Observable<PW> {
    return this.http.post<PW>(`${this.baseUrl}/api/app/company/${pw.companyId}/pw`, pw);
  }

  updatePW(pw: PW): Observable<PW> {
    return this.http.put<PW>(`${this.baseUrl}/api/app/company/${pw.companyId}/pw/${pw.id}`, pw);
  }
  exportKeywordHelper( companyId: string,formNo: string): Observable<Blob>  {
    return this.http.get(this.baseUrl + `/api/app/company/r-pTKeywords`, {
      responseType: 'blob',
      params: {
        formNo: formNo,
        companyId: companyId
      },
    });
  }


  getRecruitingList(id: string, pwId: string, filter: any, sorting: string = '', skipCount: number = 0, maxResultCount: number = 10): Observable<ListResult<RecruitmentListItem>> {
    return this.http.get<ListResult<RecruitmentListItem>>(`${this.baseUrl}/api/app/company/${id}/recruitments`, {
      params: {
        pwId,
        jobTitle: filter.jobTitle ?? '',
        pwrNumber: filter.pwrNumber ?? '',
        firstRecruitmentDate: filter.firstRecruitmentDate ?? '',
        lastRecruitmentDate: filter.lastRecruitmentDate ?? '',
        sorting,
        skipCount: skipCount.toString(),
        maxResultCount: maxResultCount.toString(),
      },
    });
  }

  getRecruitingSummary(companyId: string, id: string): Observable<RecruitmentSummary> {
    return this.http.get<RecruitmentSummary>(`${this.baseUrl}/api/app/company/${companyId}/recruitment/${id}/summary`);
  }

  getRecruiting(companyId: string, id: string): Observable<Recruitment> {
    return this.http.get<Recruitment>(`${this.baseUrl}/api/app/company/${companyId}/recruitment/${id}`);
  }

  addRecruiting(recruitment: Recruitment): Observable<Recruitment> {
    return this.http.post<Company>(`${this.baseUrl}/api/app/company/${recruitment.companyId}/recruitment`, recruitment);
  }

  updateRecruiting(recruitment: Recruitment): Observable<Recruitment> {
    return this.http.put<Recruitment>(`${this.baseUrl}/api/app/company/${recruitment.companyId}/recruitment/${recruitment.id}`, recruitment);
  }

  getRecruitmentTemplate(id: string): Observable<RecruitmentTemplate> {
    return this.http.get<RecruitmentTemplate>(`${this.baseUrl}/api/app/company/${id}/recruitment-template`);
  }

  updateRecruitmentTemplate(template: RecruitmentTemplate): Observable<RecruitmentTemplate> {
    return this.http.put<RecruitmentTemplate>(`${this.baseUrl}/api/app/company/${template.id}/recruitment-template`, template);
  }

  updateRelatedCompany(id: string, relatedId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/app/company/${id}/relation/${relatedId}`, null);
  }

  deleteRelatedCompany(id: string, relatedId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/app/company/${id}/relation/${relatedId}`);
  }
  //Detail
  updateCompany(id: string, formData: any): Observable<Company> {
    return this.http.put<Company>(`${this.baseUrl}/api/app/company/${id}`, formData);
  }

  updateCompanyInformation(id: string, formData: any): Observable<Company> {
    return this.http.put<Company>(`${this.baseUrl}/api/app/company/${id}/information`, formData);
  }

  updateCompanyStats(id: string, formData: any): Observable<Company> {
    return this.http.put<Company>(`${this.baseUrl}/api/app/company/${id}/stats`, formData);
  }

  updateCompanySupplemental(id: string, formData: any): Observable<Company> {
    return this.http.put<Company>(`${this.baseUrl}/api/app/company/${id}/supplemental`, formData);
  }

  updateCompanyDates(id: string, formData: any): Observable<Company> {
    return this.http.put<Company>(`${this.baseUrl}/api/app/company/${id}/dates`, formData);
  }

  updateCompanyCommunications(id: string, formData: any): Observable<Company> {
    return this.http.put<Company>(`${this.baseUrl}/api/app/company/${id}/communications`, formData);
  }

  //Contact
  getContacts(id: string, sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<CompanyContact>> {
    return this.http.get<ListResult<CompanyContact>>(`${this.baseUrl}/api/app/company/${id}/contacts`, {
      params: {
        id,
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  getContactsByRole(id: string, role: number, keyword: string, sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<CompanyContact>> {
    return this.http.get<ListResult<CompanyContact>>(`${this.baseUrl}/api/app/company/${id}/contacts-by-role`, {
      params: {
        role,
        keyword,
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  getContact(companyId: string, id: string): Observable<CompanyContact> {
    return this.http.get<CompanyContact>(`${this.baseUrl}/api/app/company/${companyId}/contact/${id}`);
  }

  addContact(companyContact: CompanyContact): Observable<CompanyContact> {
    return this.http.post<CompanyContact>(`${this.baseUrl}/api/app/company/${companyContact.companyId}/contact`, companyContact);
  }

  updateContact(companyContact: CompanyContact): Observable<CompanyContact> {
    return this.http.put<CompanyContact>(`${this.baseUrl}/api/app/company/${companyContact.companyId}/contact/${companyContact.id}`, companyContact);
  }

  updateContactActive(companyId: string, id: string, isActive: boolean): Observable<CompanyContact> {
    return this.http.put<CompanyContact>(`${this.baseUrl}/api/app/company/${companyId}/contact-active/${id}`, { isActive });
  }

  updateContactPrimary(companyId: string, id: string, role: number): Observable<CompanyContact> {
    return this.http.put<CompanyContact>(`${this.baseUrl}/api/app/company/${companyId}/contact-primary/${id}`, { role });
  }
//Resend verification email
  resendEmployerPortalVerification(companyId: string, contactId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/api/app/company/${companyId}/resend-employer-portal-verification`,
      { contactId }
    );
  }

  //location
  updateLocationPrimary(id: string, locationId: string): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/api/app/company/${id}/location-primary/${locationId}`, null);
  }

  addLocation(companyId: string, companyLocation: CompanyLocation): Observable<CompanyLocation> {
    return this.http.post<CompanyLocation>(`${this.baseUrl}/api/app/company/${companyId}/location`, companyLocation);
  }

  updateLocation(companyId: string, id: string, companyLocation: CompanyLocation): Observable<CompanyLocation> {
    return this.http.put<CompanyLocation>(`${this.baseUrl}/api/app/company/${companyId}/location/${id}`, companyLocation);
  }

  deleteLocation(id: string, locationId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/api/app/company/${id}/location/${locationId}`);
  }

  //ownership
  getForeignEntityList(id: string, sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<ForeignEntity>> {
    return this.http.get<ListResult<ForeignEntity>>(`${this.baseUrl}/api/app/company/${id}/foreign-entities`, {
      params: {
        id,
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  addForeignEntity(companyId: string, foreignEntity: ForeignEntity): Observable<ForeignEntity> {
    return this.http.post<ForeignEntity>(`${this.baseUrl}/api/app/company/${companyId}/foreign-entity`, foreignEntity);
  }

  updateForeignEntity(companyId: string, id: string, foreignEntity: ForeignEntity): Observable<ForeignEntity> {
    return this.http.put<ForeignEntity>(`${this.baseUrl}/api/app/company/${companyId}/foreign-entity/${id}`, foreignEntity);
  }

  getGipTeam(id: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/api/app/company/${id}/gip-team`);
  }

  updateGipTeam(id: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/api/app/company/${id}/gip-team`, data);
  }
}
